import { InputHTMLAttributes, forwardRef, useState } from 'react'
import { clsx } from 'clsx'
import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../core/hooks/useTranslation'
import { AlertCircle } from 'lucide-react'

interface EmailInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  label?: string
  error?: string
  helperText?: string
  value: string
  onChange: (value: string) => void
  required?: boolean
}

const EmailInput = forwardRef<HTMLInputElement, EmailInputProps>(
  ({ 
    className,
    label,
    error,
    helperText,
    value,
    onChange,
    required,
    disabled,
    id,
    ...props
  }, ref) => {
    const { isRTL } = useTranslation()
    const [isFocused, setIsFocused] = useState(false)
    const inputId = id || `email-input-${Math.random().toString(36).substr(2, 9)}`

    return (
      <div className="space-y-2">
        {label && (
          <label 
            htmlFor={inputId} 
            className="block text-sm text-gray-700 mb-1"
          >
            {label}
            {required && <span className="text-danger-500 mr-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          {/* Email Icon */}
          <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none z-10`}>
            <span className="text-gray-400">
              <EnvelopeIcon className="w-5 h-5" />
            </span>
          </div>
          
          {/* Input */}
          <input
            id={inputId}
            type="email"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            className={clsx(
              'input',
              error && 'input-error',
              isRTL ? 'pr-10' : 'pl-10',
              error && 'pr-10',
              className
            )}
            ref={ref}
            placeholder={isRTL ? 'example@email.com' : 'example@email.com'}
            {...props}
          />
          
          {/* Error Icon */}
          {error && (
            <div className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center pointer-events-none`}>
              <AlertCircle className="w-5 h-5 text-danger-500" />
            </div>
          )}
        </div>
        
        {error && (
          <div className="flex items-center space-x-1 rtl:space-x-reverse">
            <AlertCircle className="w-4 h-4 text-danger-500 flex-shrink-0" />
            <p className="text-sm text-danger-600 font-medium">{error}</p>
          </div>
        )}
        
        {helperText && !error && (
          <p className="text-sm text-secondary-500">{helperText}</p>
        )}
      </div>
    )
  }
)

EmailInput.displayName = 'EmailInput'

export default EmailInput

